import classNames from 'classnames';
import React, {
  ChangeEventHandler,
  ElementType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { WrappedFieldProps } from 'redux-form';
import { numberNormalizer } from '../helpers/utils';
import Label from './Label';

interface InputProps extends WrappedFieldProps {
  label?: string;
  tag?: ElementType;
  className?: string;
  type: string;
  autofocus?: boolean;
  icon?: ElementType;
  initialValue?: { [key: string]: any };
  onInputChange?: ChangeEventHandler<any>;
}

const Input = (props: InputProps) => {
  const {
    input: { value, onChange, onBlur, ...input },
    meta: { error = false } = {},
    icon: Icon = null,
    autofocus = false,
    tag: Tag = 'input',
    type,
    className,
    onInputChange,
    initialValue = '',
    ...restProps
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autofocus]);
  const [displayValue, setDisplayValue] = useState(initialValue);
  const nextOnInputChange = useCallback(
    evt => {
      const rawValue = evt.target.value;
      if (type === 'number') {
        // To be displayed
        const value = numberNormalizer(rawValue);
        setDisplayValue(value);

        // To be stored
        onChange(value.replace(/,/g, ''));
      } else {
        setDisplayValue(rawValue);
        onChange(rawValue);
      }
      if (onInputChange) {
        onInputChange(evt);
      }
    },
    [onChange, onInputChange, type]
  );

  const onInputBlur = useCallback(() => {
    onBlur(value);
  }, [onBlur, value]);

  const nextType = useMemo(() => {
    if (type === 'number') {
      return 'text';
    }
    return type;
  }, [type]);

  return (
    <Label className={className} {...props}>
      <Tag
        ref={inputRef}
        className={classNames(
          'py-3 pr-2 md:py-4 md:pr-4 rounded-xs bg-gray-200 text-gray-900 outline-none',
          { 'border border-red-600': error },
          Icon ? 'pl-10 md:pl-12' : 'pl-3 md:pl-4'
        )}
        {...input}
        {...restProps}
        type={nextType}
        value={displayValue}
        onChange={nextOnInputChange}
        onBlur={onInputBlur}
        autoComplete={type === 'password' ? 'new-password' : undefined}
      />
    </Label>
  );
};

export default Input;
